import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { isProductSourceUploadAndIngestionComplete } from '@/features/product-catalog/utils/productSourceUploadStatus'
import {
  useListProductSources,
  type ProductSource,
} from '@/shared/hooks/api/productSource'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'

type RiskReductionMeasureProofSourceSelectDialogProps = {
  productId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  linkedSourceIds: string[]
  onConfirm: (sources: ProductSource[]) => void
  isSubmitting: boolean
}

export function RiskReductionMeasureProofSourceSelectDialog(
  props: RiskReductionMeasureProofSourceSelectDialogProps
) {
  const {
    productId,
    open,
    onOpenChange,
    linkedSourceIds,
    onConfirm,
    isSubmitting,
  } = props
  const { t } = useTranslation('machineryDirectiveCompliance')
  const { data: sources = [], isLoading, isError } = useListProductSources(
    open ? productId : undefined
  )

  const selectableSources = useMemo(
    () =>
      sources.filter(
        (source) =>
          isProductSourceUploadAndIngestionComplete(source) &&
          !linkedSourceIds.includes(source.id)
      ),
    [linkedSourceIds, sources]
  )

  const [pendingSourceIds, setPendingSourceIds] = useState<string[]>([])

  useEffect(() => {
    if (!open) {
      setPendingSourceIds([])
    }
  }, [open])

  const toggleSource = (sourceId: string, checked: boolean) => {
    if (checked) {
      setPendingSourceIds((current) => [...current, sourceId])
      return
    }

    setPendingSourceIds((current) =>
      current.filter((currentId) => currentId !== sourceId)
    )
  }

  const handleConfirm = () => {
    const selectedSources = selectableSources.filter((source) =>
      pendingSourceIds.includes(source.id)
    )
    if (selectedSources.length === 0) {
      return
    }

    onConfirm(selectedSources)
  }

  const showLoading = isLoading
  const showError = !isLoading && isError
  const showEmpty = !isLoading && !isError && selectableSources.length === 0
  const showSourceList = !isLoading && !isError && selectableSources.length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>
            {t(
              'riskReductionMeasuresLibraryPage.implementation.proofSourceDialog.title'
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              'riskReductionMeasuresLibraryPage.implementation.proofSourceDialog.description'
            )}
          </DialogDescription>
        </DialogHeader>

        {showLoading && (
          <div className='flex items-center justify-center py-8'>
            <Loader2 className='size-6 animate-spin text-muted-foreground' />
          </div>
        )}

        {showError && (
          <p className='text-sm text-destructive'>
            {t(
              'riskReductionMeasuresLibraryPage.implementation.proofSourceDialog.loadError'
            )}
          </p>
        )}

        {showEmpty && (
          <p className='text-sm text-muted-foreground'>
            {t(
              'riskReductionMeasuresLibraryPage.implementation.proofSourceDialog.empty'
            )}
          </p>
        )}

        {showSourceList && (
          <div className='max-h-64 space-y-2 overflow-y-auto pe-1'>
            {selectableSources.map((source) => {
              const checkboxId = `rrm-proof-source-${source.id}`
              const isChecked = pendingSourceIds.includes(source.id)

              return (
                <div
                  key={source.id}
                  className='flex items-start gap-3 rounded-lg border p-3'
                >
                  <Checkbox
                    id={checkboxId}
                    checked={isChecked}
                    className='mt-0.5'
                    onCheckedChange={(checked) => {
                      toggleSource(source.id, checked === true)
                    }}
                    disabled={isSubmitting}
                  />
                  <Label
                    htmlFor={checkboxId}
                    className='min-w-0 flex-1 cursor-pointer space-y-1 font-normal'
                  >
                    <span className='block text-sm font-medium text-foreground'>
                      {source.name}
                    </span>
                    {source.classification != null && (
                      <span className='block text-xs text-muted-foreground'>
                        {source.classification.replace(/_/g, ' ')}
                      </span>
                    )}
                  </Label>
                </div>
              )
            })}
          </div>
        )}

        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={() => {
              onOpenChange(false)
            }}
            disabled={isSubmitting}
          >
            {t(
              'riskReductionMeasuresLibraryPage.implementation.proofSourceDialog.cancel'
            )}
          </Button>
          <Button
            type='button'
            onClick={handleConfirm}
            disabled={isSubmitting || pendingSourceIds.length === 0}
          >
            {isSubmitting ? <Loader2 className='size-4 animate-spin' /> : null}
            {t(
              'riskReductionMeasuresLibraryPage.implementation.proofSourceDialog.confirm'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
